/**
 * ScheduledMailView
 *
 * Shows all scheduled emails for the current user.
 * Displayed in the right pane when the virtual "Scheduled" folder is selected.
 * Supports: view details, reschedule, cancel.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DatePicker, Modal, Spin, Tooltip, message as antMsg } from 'antd';
import dayjs from 'dayjs';
import {
  CalendarOutlined, CloseOutlined, ClockCircleOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, StopOutlined,
} from '@ant-design/icons';

import {
  fetchScheduledEmails,
  cancelScheduledEmail,
  rescheduleEmail,
  selectScheduledItems,
  selectScheduledStatus,
} from '../../../Slices/scheduledMailSlice';

const FONT = "'Segoe UI', Tahoma, sans-serif";
const C = { blue: '#0f6cbd', border: '#edebe9', bg: '#faf9f8', text: '#242424', muted: '#8a8886' };

const STATUS_META = {
  pending:   { label: 'Scheduled', color: '#0f6cbd', icon: <ClockCircleOutlined /> },
  sent:      { label: 'Sent',      color: '#107c10', icon: <CheckCircleOutlined /> },
  failed:    { label: 'Failed',    color: '#c50f1f', icon: <ExclamationCircleOutlined /> },
  cancelled: { label: 'Cancelled', color: '#8a8886', icon: <StopOutlined /> },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10,
      background: `${meta.color}18`, color: meta.color,
      fontSize: 12, fontWeight: 600,
    }}>
      {meta.icon} {meta.label}
    </span>
  );
}

function RecipientList({ addrs }) {
  if (!addrs?.length) return <span style={{ color: C.muted }}>—</span>;
  return <span>{addrs.join(', ')}</span>;
}

export default function ScheduledMailView() {
  const dispatch = useDispatch();
  const items    = useSelector(selectScheduledItems);
  const status   = useSelector(selectScheduledStatus);

  // Reschedule modal state
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // { id, scheduledAt }
  const [pickerValue,      setPickerValue]      = useState(null);
  const [rescheduling,     setRescheduling]     = useState(false);

  // Cancel confirmation
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling,   setCancelling]   = useState(false);

  useEffect(() => {
    dispatch(fetchScheduledEmails());
  }, [dispatch]);

  const handleCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await dispatch(cancelScheduledEmail({ id: cancelTarget.id })).unwrap();
      antMsg.success('Scheduled email cancelled');
    } catch (err) {
      antMsg.error(err || 'Failed to cancel');
    } finally {
      setCancelling(false);
      setCancelTarget(null);
    }
  }, [cancelTarget, dispatch]);

  const handleReschedule = useCallback(async () => {
    if (!rescheduleTarget || !pickerValue) return;
    setRescheduling(true);
    try {
      await dispatch(rescheduleEmail({
        id: rescheduleTarget.id,
        scheduledAt: pickerValue.toISOString(),
      })).unwrap();
      antMsg.success('Rescheduled successfully');
      setRescheduleTarget(null);
      setPickerValue(null);
      dispatch(fetchScheduledEmails());
    } catch (err) {
      antMsg.error(err || 'Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  }, [rescheduleTarget, pickerValue, dispatch]);

  const pending   = items.filter(i => i.status === 'pending');
  const completed = items.filter(i => i.status !== 'pending');

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      fontFamily: FONT, background: C.bg, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px 12px',
        borderBottom: `1px solid ${C.border}`,
        background: '#fff',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CalendarOutlined style={{ fontSize: 20, color: C.blue }} />
          <span style={{ fontSize: 18, fontWeight: 600, color: C.text }}>Scheduled</span>
          {pending.length > 0 && (
            <span style={{
              background: C.blue, color: '#fff', fontSize: 11, fontWeight: 700,
              minWidth: 20, height: 20, borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 5px',
            }}>{pending.length}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          Emails queued for automatic delivery
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {status === 'loading' && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
            <Spin />
          </div>
        )}

        {status === 'loaded' && items.length === 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            height: '60%', gap: 10, color: C.muted,
          }}>
            <CalendarOutlined style={{ fontSize: 48, color: '#C8C6C4' }} />
            <span style={{ fontSize: 15, color: '#323130' }}>No scheduled emails</span>
            <span style={{ fontSize: 13 }}>Use "Schedule Send" when composing a message</span>
          </div>
        )}

        {/* Pending */}
        {pending.length > 0 && (
          <Section title="Pending">
            {pending.map(item => (
              <ScheduledRow
                key={item.id}
                item={item}
                onCancel={() => setCancelTarget(item)}
                onReschedule={() => {
                  setRescheduleTarget(item);
                  setPickerValue(dayjs(item.scheduledAt));
                }}
              />
            ))}
          </Section>
        )}

        {/* History */}
        {completed.length > 0 && (
          <Section title="History">
            {completed.map(item => (
              <ScheduledRow key={item.id} item={item} readonly />
            ))}
          </Section>
        )}
      </div>

      {/* Cancel confirm modal */}
      <Modal
        open={!!cancelTarget}
        title="Cancel scheduled email?"
        okText="Cancel send"
        okButtonProps={{ danger: true, loading: cancelling }}
        cancelText="Keep"
        onOk={handleCancel}
        onCancel={() => setCancelTarget(null)}
        width={420}
      >
        <p style={{ fontFamily: FONT, color: C.text }}>
          "{cancelTarget?.subject || '(No subject)'}" will be deleted and not sent.
        </p>
      </Modal>

      {/* Reschedule modal */}
      <Modal
        open={!!rescheduleTarget}
        title="Reschedule email"
        okText="Reschedule"
        okButtonProps={{ loading: rescheduling, disabled: !pickerValue }}
        onOk={handleReschedule}
        onCancel={() => { setRescheduleTarget(null); setPickerValue(null); }}
        width={420}
      >
        <p style={{ fontFamily: FONT, color: C.muted, marginBottom: 10 }}>
          Choose a new send time for "{rescheduleTarget?.subject || '(No subject)'}":
        </p>
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 1 }}
          format="MMM D, YYYY h:mm A"
          value={pickerValue}
          onChange={setPickerValue}
          disabledDate={d => d && d.isBefore(dayjs().startOf('day'))}
          style={{ width: '100%' }}
        />
      </Modal>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: C.muted,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        marginBottom: 6, paddingLeft: 4,
      }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

function ScheduledRow({ item, onCancel, onReschedule, readonly }) {
  const scheduledStr = item.scheduledAt ? dayjs(item.scheduledAt).format('MMM D, YYYY h:mm A') : '—';

  return (
    <div style={{
      background: '#fff',
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      padding: '10px 14px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.subject || '(No subject)'}
          </span>
          <StatusBadge status={item.status} />
        </div>
        <div style={{ fontSize: 12, color: C.muted }}>
          To: <RecipientList addrs={item.to} />
          {item.cc?.length > 0 && <span> · CC: <RecipientList addrs={item.cc} /></span>}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          <CalendarOutlined style={{ marginRight: 4 }} />
          {item.status === 'sent' && item.sentAt
            ? `Sent ${dayjs(item.sentAt).format('MMM D, YYYY h:mm A')}`
            : `Send at ${scheduledStr}`}
          {item.error && <span style={{ color: '#c50f1f', marginLeft: 8 }}>— {item.error}</span>}
        </div>
      </div>

      {!readonly && item.status === 'pending' && (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <Tooltip title="Reschedule">
            <button
              onClick={onReschedule}
              style={{
                border: `1px solid ${C.border}`, borderRadius: 4,
                background: '#fff', cursor: 'pointer', padding: '4px 8px',
                color: C.blue, fontSize: 12, fontFamily: FONT,
              }}
            >
              <CalendarOutlined /> Reschedule
            </button>
          </Tooltip>
          <Tooltip title="Cancel send">
            <button
              onClick={onCancel}
              style={{
                border: '1px solid #fde7e9', borderRadius: 4,
                background: '#fff', cursor: 'pointer', padding: '4px 8px',
                color: '#c50f1f', fontSize: 12, fontFamily: FONT,
              }}
            >
              <CloseOutlined /> Cancel
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
